import { formatDateTime } from "@/utils/dateTime";
import { getGoodsPricing } from "@/utils/goodsPricing";
import PageTab from "@/components/i-pageTab/i-pageTab";
import { useEffect, useState } from "react";
import type { ComponentProps, ReactElement } from "react";
import {
  Button,
  Form,
  Image,
  message,
  Modal,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { deleteGoods, exportGoods, getGoods, listGoods } from "@/api/shopGoods";
import type {
  EntityId,
  GoodsDetail,
  GoodsQuery,
  GoodsRow,
} from "@/types/shopGoods";
import { useShopList } from "../useShopList";
import { GoodsEditor } from "./GoodsEditor";
import { GoodsDetailPanel } from "./GoodsDetailPanel";
import { GoodsActionDialog } from "./GoodsActionDialog";
import { GoodsSortInput } from "./GoodsSortInput";
import type { GoodsAction } from "./GoodsActionDialog";
import { canChangeGoodsShelf, canShelfGoods } from "./goodsForm";
import styles from "./ProductDialog.module.css";
import {
  goodsTypes,
  stockTypes,
  goodsAuditStatuses,
  statusOptions,
  goodsPriceLabel,
  goodsSettlementPriceLabel,
  getErrorMessage,
} from "./goodsLabels";

interface ProductProps {
  mode?: "all" | "selfOperated" | "settledMerchant";
}
type Dialog = { mode: "add" } | { mode: "detail" | "edit"; id: EntityId; merchantName?: string | null };
type SearchValues = Omit<GoodsQuery, "ypageNum" | "ypageSize">;
const goodsTypeDisplay: Record<number, { label: string; color: string }> = {
  1: { label: "自营", color: "blue" },
  2: { label: "入驻商家", color: "purple" },
};

function Product({ mode = "all" }: ProductProps): ReactElement {
  const [form] = Form.useForm<SearchValues>();
  const fixedType =
    mode === "settledMerchant" ? 2 : mode === "selfOperated" ? 1 : undefined;
  const initialQuery: GoodsQuery = {
    ypageNum: 1,
    ypageSize: 10,
    goodsType: fixedType,
  };
  const [query, setQuery] = useState<GoodsQuery>(initialQuery);
  const list = useShopList(listGoods, query);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [detail, setDetail] = useState<GoodsDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [action, setAction] = useState<GoodsAction | null>(null);

  useEffect(() => {
    setDetail(null);
    if (!dialog || dialog.mode === "add") return;
    const controller = new AbortController();
    setDetailLoading(true);
    void getGoods(dialog.id, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setDetail({
          ...response.data,
          merchantName: response.data.merchantName?.trim() || dialog.merchantName,
        });
      })
      .catch(() => {
        // 请求层统一提示错误。
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });
    return (): void => controller.abort();
  }, [dialog]);

  const handleClose = (): void => {
    if (!saving) setDialog(null);
  };
  const handleSaved = (): void => {
    setDialog(null);
    if (dialog?.mode === "add")
      setQuery((current) => ({ ...current, ypageNum: 1 }));
    else list.refresh();
  };
  const handleSearch = (values: SearchValues): void => {
    setQuery({
      ...initialQuery,
      ypageSize: query.ypageSize,
      goodsName: values.goodsName?.trim() || undefined,
      goodsType: fixedType ?? values.goodsType,
      merchantName: values.merchantName?.trim() || undefined,
      selfSupplier: values.selfSupplier?.trim() || undefined,
      auditStatus: values.auditStatus ?? undefined,
      isShelf: values.isShelf ?? undefined,
    });
  };
  const handleReset = (): void => {
    form.resetFields();
    setQuery(initialQuery);
  };
  const handleExport = async (): Promise<void> => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await exportGoods(query);
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement("a");
        link.href = url;
        link.download = "商城商品.xlsx";
        link.click();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (error: unknown) {
      void message.error(getErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };
  const handleAdd = (): void => setDialog({ mode: "add" });
  const formTab: ComponentProps<typeof PageTab>["formTab"] = [
    { label: "商品名称", name: "goodsName", type: "input", params: { allowClear: true } },
    { label: "商品类型", name: "goodsType", type: "select", options: goodsTypes, show: fixedType === undefined, params: { placeholder: "请选择商品类型" } },
    { label: "商家名称", name: "merchantName", type: "input", params: { allowClear: true } },
    { label: "自营供应商", name: "selfSupplier", type: "input", params: { allowClear: true } },
    { label: "审核状态", name: "auditStatus", type: "select", options: statusOptions(goodsAuditStatuses), params: { placeholder: "请选择审核状态" } },
    { label: "上架状态", name: "isShelf", type: "select", options: statusOptions(["下架", "上架"]), params: { placeholder: "请选择上架状态" } },
  ];
  const footer = [
    ...(mode !== "settledMerchant" ? [{ title: "新增商品", type: "primary", onClick: handleAdd }] : []),
    { title: "导出", onClick: handleExport, loading: exporting },
  ];
  const handleActionSaved = (): void => {
    message.success(action?.type === "approve" ? "审核通过，请按需手动上架" : action?.type === "reject" ? "商品已驳回" : "商品上下架状态已更新");
    setAction(null);
    list.refresh();
  };
  const handleShelfChange = (row: GoodsRow): void => {
    if (list.loading || action) return;
    if (!canChangeGoodsShelf(row)) return;
    setAction({ type: "shelf", row });
  };
  const handleEdit = (row: GoodsRow): void => {
    if (list.loading || action || row.isShelf === 1) return;
    setDialog({ mode: "edit", id: row.id });
  };
  const handleDelete = (row: GoodsRow): void => {
    if (row.isShelf === 1) {
      void message.warning("请先下架商品后再删除");
      return;
    }
    Modal.confirm({
      title: "删除商品",
      content: `确认删除“${row.goodsName}”？商品的轮播图、SKU、规格和分类关联将一并删除。`,
      onOk: async (): Promise<void> => {
        await deleteGoods(row.id);
        message.success("商品已删除");
        if (list.rows.length === 1 && query.ypageNum > 1)
          setQuery((current) => ({
            ...current,
            ypageNum: current.ypageNum - 1,
          }));
        else list.refresh();
      },
    });
  };
  const columns: ColumnsType<GoodsRow> = [
    { title: "商品ID", dataIndex: "id" },
    {
      title: "商品主图",
      render: (_, row): ReactElement | string =>
        row.mainImage ? <Image width={64} src={row.mainImage} /> : "-",
    },
    { title: "商品名称", dataIndex: "goodsName" },
    {
      title: "商品类型",
      render: (_, row): ReactElement => {
        const display = goodsTypeDisplay[row.goodsType];
        return (
          <Tag color={display?.color ?? "default"}>
            {display?.label ?? row.goodsTypeName ?? "未知"}
          </Tag>
        );
      },
    },
    {
      title: "库存类型",
      render: (_, row): ReactElement => (
        <Tag color={["default", "green", "orange"][row.stockType] ?? "default"}>
          {stockTypes.find((item) => item.value === row.stockType)?.label || "未知"}
        </Tag>
      ),
    },
    // { title: "预售结束/可开始发货时间", dataIndex: "preSaleTime", render: formatDateTime },
    { title: "售价", render: (_, row): string => goodsPriceLabel(row) },
    { title: "结算价", render: (_, row): string => goodsSettlementPriceLabel(row) },
    { title: "可用库存", render: (_, row): number | string => getGoodsPricing(row).stock ?? "-" },
    { title: "商品总销量", dataIndex: "salesNum", render: (value: GoodsRow["salesNum"]): number | string => value ?? "-" },
    {
      title: "上架状态",
      render: (_, row): ReactElement => (
        <Switch
          checked={row.isShelf === 1}
          checkedChildren="上架"
          unCheckedChildren="下架"
          aria-label={`${row.goodsName}上架状态`}
          disabled={list.loading || action !== null || !canChangeGoodsShelf(row)}
          title={row.goodsType === 1 ? undefined : row.auditStatus === 1 ? "审核中不允许上下架" : row.isShelf === 0 && !canShelfGoods(row.auditStatus, row.goodsType) ? "审核通过后才能上架" : undefined}
          onChange={() => handleShelfChange(row)}
        />
      ),
    },
    {
      title: "审核状态",
      render: (_, row): ReactElement => (
        <Tag color={row.goodsType === 1 ? "default" : ["default", "orange", "green", "red"][row.auditStatus] ?? "default"}>
          {row.goodsType === 1 ? "无需审核" : goodsAuditStatuses[row.auditStatus] ?? "未知"}
        </Tag>
      ),
    },
    { title: "商家名称", dataIndex: "merchantName" },
    { title: "自营供应商", dataIndex: "selfSupplier", render: (value: GoodsRow["selfSupplier"]): string => value?.trim() || "-" },
    {
      title: "排序",
      dataIndex: "sort",
      width: 140,
      render: (_, row): ReactElement => (
        <GoodsSortInput
          key={`${row.id}:${row.sort}`}
          row={row}
          disabled={list.loading || action !== null}
          onSaved={list.refresh}
        />
      ),
    },
    { title: "添加时间", dataIndex: "createTime", render: formatDateTime },
    {
      title: "操作",
      fixed: "right",
      render: (_, row): ReactElement => (
        <Space>
          <Button
            size="small"
            onClick={() => setDialog({ mode: "detail", id: row.id, merchantName: row.merchantName })}
          >
            详情
          </Button>
          <Tooltip title={row.isShelf === 1 ? "请先下架商品后再编辑" : undefined}>
            <span>
              <Button
                size="small"
                type="primary"
                disabled={list.loading || action !== null || row.isShelf === 1}
                onClick={() => handleEdit(row)}
              >
                编辑
              </Button>
            </span>
          </Tooltip>
          {row.goodsType !== 1 && row.auditStatus === 1 && <>
            <Button size="small" disabled={list.loading} onClick={() => setAction({ type: "approve", row })}>审核通过</Button>
            <Button size="small" danger disabled={list.loading} onClick={() => setAction({ type: "reject", row })}>审核驳回</Button>
          </>}
          <Tooltip title={row.isShelf === 1 ? "请先下架商品后再删除" : undefined}>
          <span><Button size="small" danger disabled={list.loading || row.isShelf === 1} onClick={() => handleDelete(row)}>
            删除
          </Button></span>
          </Tooltip>
        </Space>
      ),
    },
  ];
  return (
    <div>
      <PageTab
        form={form}
        formTab={formTab}
        search={handleSearch}
        reset={handleReset}
        footer={footer}
        defaultTimer={null}
      />
      <Table<GoodsRow>
        rowKey="id"
        style={{ marginTop: 20 }}
        columns={columns}
        dataSource={list.rows}
        loading={list.loading}
        scroll={{ x: "max-content" }}
        pagination={{
          current: query.ypageNum,
          pageSize: query.ypageSize,
          total: list.total,
          showSizeChanger: true,
          onChange: (ypageNum, ypageSize): void =>
            setQuery((current) => ({ ...current, ypageNum, ypageSize })),
        }}
      />
      {action && <GoodsActionDialog
        action={action}
        onSaved={handleActionSaved}
        onCancel={() => setAction(null)}
      />}
      <Modal
        className={styles.modal}
        title={
          dialog?.mode === "add"
            ? "新增商品"
            : dialog?.mode === "edit"
              ? "编辑商品"
              : "商品详情"
        }
        open={dialog !== null}
        width={1100}
        footer={null}
        onCancel={handleClose}
        destroyOnHidden
        maskClosable={false}
        closable={!saving}
        keyboard={!saving}
      >
        {dialog?.mode === "add" ? (
          <GoodsEditor
            onSaved={handleSaved}
            onCancel={handleClose}
            onBusyChange={setSaving}
          />
        ) : (
          <Spin spinning={detailLoading} wrapperClassName={styles.dialogState}>
            {detail &&
              (dialog?.mode === "edit" ? (
                <GoodsEditor
                  key={detail.id}
                  detail={detail}
                  onSaved={handleSaved}
                  onCancel={handleClose}
                  onBusyChange={setSaving}
                />
              ) : (
                <div className={styles.detailLayout}>
                  <div className={styles.scrollBody}>
                    <GoodsDetailPanel detail={detail} />
                  </div>
                  <div className={styles.actions}>
                    <Button onClick={handleClose}>关闭</Button>
                  </div>
                </div>
              ))}
          </Spin>
        )}
      </Modal>
    </div>
  );
}
export default Product;
